import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useAchievements, EGGS } from "@/os/state/achievements";
import { useSettings, SLIDER_DEFAULTS } from "@/os/state/settings";
import { useTheme } from "@/os/state/theme";
import { awayLabel, visitInfo } from "@/os/state/session";
import { SquiggleStrike } from "@/components/decor";

const LINES = [
    "krithi.os v7.0 — cold boot",
    "checking memory .......... 37 tabs found",
    "2,000+ applications detected.",
    "standard resume format critically overused.",
    "searching for a more interesting way to introduce krithi ...",
];

export function BootScreen({ onFinish }: { onFinish: (target: "desktop" | "resume") => void }) {
    const reduce = useReducedMotion();
    const { trackEgg, eggs } = useAchievements();
    const { sliders, modes } = useSettings();
    const { theme } = useTheme();
    const visit = useMemo(() => visitInfo(), []);

    const lines = useMemo(() => {
        if (!visit.returning) return LINES;
        const tweaked = (Object.keys(SLIDER_DEFAULTS) as (keyof typeof SLIDER_DEFAULTS)[]).filter(
            (k) => sliders[k] !== SLIDER_DEFAULTS[k],
        );
        const mode = modes.chaotic
            ? "chaotic research mode restored. the tabs never left."
            : modes.bedtime
              ? "bedtime conflict restored. curiosity still wants one more tab."
              : modes.impatient
                ? "performance impatience restored. loading screens remain unwelcome."
                : tweaked.length > 0
                  ? "no combination modes active — you left it calibrated."
                  : "personality untouched since last time. factory krithi.";
        return [
            `krithi.os v7.0 — warm boot (visit #${visit.visits})`,
            `welcome back. last session: ${awayLabel(visit.daysAway)}`,
            `restoring personality ... curiosity ${sliders.curiosity} · overthinking ${sliders.overthinking} · sleep ${sliders.sleep} · patience ${sliders.tolerance}`,
            mode,
            `wallpaper: ${theme} · easter eggs found: ${eggs.length}/${EGGS.length}${eggs.length < EGGS.length ? " (the rest are still hiding)" : " (all of them. show-off.)"}`,
            "37 tabs restored exactly as you abandoned them.",
        ];
    }, [visit, sliders, modes, theme, eggs.length]);

    const [shown, setShown] = useState(reduce ? lines.length : 0);
    const [phase, setPhase] = useState<"boot" | "error">(reduce ? "error" : "boot");
    const [wobble, setWobble] = useState(false);
    const finishRef = useRef(onFinish);
    finishRef.current = onFinish;

    useEffect(() => {
        if (reduce) return;
        if (shown < lines.length) {
            const t = setTimeout(() => setShown((s) => s + 1), shown === 0 ? 350 : 400);
            return () => clearTimeout(t);
        }
        const t = setTimeout(() => setPhase("error"), 620);
        return () => clearTimeout(t);
    }, [shown, reduce, lines.length]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                e.preventDefault();
                finishRef.current("desktop");
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const hit404 = () => {
        trackEgg("egg_boot_404");
        setWobble(true);
        setTimeout(() => setWobble(false), 450);
    };

    return (
        <div className="fixed inset-0 z-[5000] overflow-auto bg-cream" data-testid="boot-screen">
            <button
                data-testid="boot-skip"
                onClick={() => finishRef.current("desktop")}
                className="btn btn-ghost btn-sm absolute right-5 top-5"
            >
                skip intro
            </button>
            <div className="mx-auto flex min-h-full w-full max-w-2xl flex-col justify-center px-6 py-12">
                <div className="flex items-center gap-3">
                    <img src="/logo.svg" alt="" aria-hidden className="h-9 w-9" />
                    <span className="font-mono text-sm font-bold tracking-tight text-ink">KRITHI.OS</span>
                </div>

                {phase === "boot" ? (
                    <div className="mt-10 font-mono text-[13px] leading-7 text-inksoft" aria-live="polite">
                        {lines.slice(0, shown).map((l) => (
                            <motion.p key={l} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                {l}
                            </motion.p>
                        ))}
                        <span className="caret" aria-hidden />
                        <div className="mt-8 h-2 w-full max-w-sm overflow-hidden rounded-full border border-ink/20 bg-paper">
                            <div
                                className="h-full bg-elec transition-all duration-500"
                                style={{ width: `${(shown / lines.length) * 100}%` }}
                            />
                        </div>
                    </div>
                ) : (
                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                    >
                        <p className="mt-10 font-mono text-xs uppercase tracking-widest text-inkfaint">
                            {visit.returning ? `system message · session restored (visit #${visit.visits})` : "system message"}
                        </p>
                        <h1 className="mt-3 font-display text-6xl font-extrabold tracking-tight text-ink sm:text-7xl">
                            ERROR{" "}
                            <button
                                data-testid="boot-egg-404"
                                onClick={hit404}
                                className={`text-coral ${wobble ? "wiggle" : ""}`}
                                aria-label="Error 404"
                            >
                                404
                            </button>
                        </h1>
                        <p className="mt-3 font-display text-2xl font-bold text-ink sm:text-3xl">
                            Standard{" "}
                            <span className="relative inline-block">
                                resume
                                <SquiggleStrike className="absolute -bottom-1.5 left-0 h-2 w-full" />
                            </span>{" "}
                            not found.
                        </p>
                        <p className="mt-3 max-w-md text-sm text-inksoft">
                            {visit.returning
                                ? `still not standard, and your personality is exactly where you left it — curiosity ${sliders.curiosity}, overthinking ${sliders.overthinking}. two ways back in:`
                                : "the resume exists though — it's just not standard. two ways in:"}
                        </p>
                        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                            <button
                                data-testid="boot-open-krithi-exe"
                                className="btn btn-blue"
                                onClick={() => finishRef.current("desktop")}
                            >
                                {visit.returning ? "Resume krithi.exe" : "Open krithi.exe"}
                            </button>
                            <button
                                data-testid="boot-open-resume"
                                className="btn"
                                onClick={() => finishRef.current("resume")}
                            >
                                Just show me the resume
                            </button>
                        </div>
                        <p className="mt-6 font-mono text-[11px] text-inkfaint">tip: press Enter to skip</p>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
